// The boot's schema window — is this a shape this bundle can read?
//
// 🔴 ASK-131 §2b. The boot carries no version today and NOT ONE of the four client fetch sites
// checks for one; all four check `r.ok` and stop. Worse, the 25-second live-refresh poller merges
// whatever arrived by blind spread (`app.js` `_performLiveRefresh`), so a truncated, malformed or
// breaking-renamed boot overwrites good in-session state within 25 seconds — and the sha-drift
// detector next to it can only ever say "the boot changed", never "this is a shape I can read".
// Fixture and product manifests have had a declared v2/v3/v4 window since May
// (`engine/manifest-loader.js`); the artifact everything else hangs off had less contract
// discipline than the things it points at.
//
// 🔴 PERMISSIVE-FIRST, AND THE ORDER IS LOAD-BEARING (ASK-131 §7b). Client ships this BEFORE
// Content stamps the field. A client that refused a boot without a version would refuse TODAY'S
// LIVE BOOT and blank every shop on the plane the moment it deployed. So:
//
//     ABSENT  → legacy, ACCEPT. Absence is "no opinion", never a refusal (§6 absent-vs-empty).
//     KNOWN   → accept.
//     FUTURE  → refuse: a major this bundle predates is exactly what a version field is for.
//     GARBAGE → refuse: a version that is not a number is not a version.
//     SHAPELESS → refuse: no `shops` object is not a boot, whatever its version says.
//
// 📌 THE WINDOW IS A CONTRACT WITH CONTENT, and it is stated here so the number is not folklore:
// **Content stamps `schemaVersion: 1`.** Written to coord alongside this ship. A newer shape than
// this bundle knows is refused BY DESIGN — that is the whole point — so raising BOOT_SCHEMA_MAX is
// how a future client opts into a future boot, and both numbers move in the same pass as the shape.
//
// Precedent: Unity Addressables' catalog version, OCI image-manifest `schemaVersion`, and our own
// manifest window three files over. Pure — no fetch, no DOM; `test-boot-schema` covers every branch.

/** The newest boot shape this bundle knows how to read. Raise it in the same pass as the change. */
export const BOOT_SCHEMA_MAX = 1;
/** The oldest still readable. Below this the shape predates something we rely on. */
export const BOOT_SCHEMA_MIN = 1;

/**
 * @returns {{ ok: boolean, version: number|null, reason: string|null, detail: string }}
 *   `reason` is null when the boot is versioned and supported, "legacy" when it carries no version
 *   (accepted), and one of "shapeless" | "unreadable" | "future" | "ancient" on a refusal.
 */
export function readBootSchema(boot) {
	if (!boot || typeof boot !== "object" || Array.isArray(boot)) {
		return { ok: false, version: null, reason: "shapeless", detail: `boot is ${boot === null ? "null" : typeof boot}, not an object` };
	}
	// A boot without a `shops` map is not a boot — this is what catches a breaking rename or an
	// error envelope that happens to be valid JSON, which `r.ok` and `.json()` both wave through.
	if (!boot.shops || typeof boot.shops !== "object" || Array.isArray(boot.shops)) {
		return { ok: false, version: null, reason: "shapeless", detail: "no `shops` object" };
	}

	const raw = boot.schemaVersion ?? boot.version ?? null;
	if (raw === null || raw === undefined) {
		return { ok: true, version: null, reason: "legacy", detail: "no schemaVersion — pre-stamp boot, accepted" };
	}
	// Accept the number in either form. Content stamping `"1"` instead of `1` is not a shape this
	// bundle cannot read, and refusing over it would be pedantry with the plane as the stake.
	const n = typeof raw === "number" ? raw : (typeof raw === "string" && raw.trim() !== "" ? Number(raw) : NaN);
	if (!Number.isFinite(n)) {
		return { ok: false, version: null, reason: "unreadable", detail: `schemaVersion ${JSON.stringify(raw)} is not a number` };
	}
	if (n > BOOT_SCHEMA_MAX) {
		return { ok: false, version: n, reason: "future", detail: `boot schemaVersion ${n} is newer than this bundle reads (max ${BOOT_SCHEMA_MAX})` };
	}
	if (n < BOOT_SCHEMA_MIN) {
		return { ok: false, version: n, reason: "ancient", detail: `boot schemaVersion ${n} predates this bundle's window (min ${BOOT_SCHEMA_MIN})` };
	}
	return { ok: true, version: n, reason: null, detail: `schemaVersion ${n}` };
}

/** One sentence a human can act on, for the console and for the failure surface. */
export function describeBootSchema(verdict) {
	if (!verdict) return "boot schema not checked";
	return verdict.ok
		? `boot schema OK — ${verdict.detail}`
		: `boot REFUSED — ${verdict.detail}`;
}
